import api from './api';

export interface ProfitLossData {
  period: {
    startDate: string;
    endDate: string;
  };
  revenue: number;
  expenses: {
    operating: number;
    payroll: number;
    depreciation: number;
    total: number;
  };
  profit: number;
  profitMargin: number;
}

export interface BalanceSheetData {
  asOfDate: string;
  assets: {
    cash: number;
    accountsReceivable: number;
    fixedAssets: {
      atCost: number;
      accumulatedDepreciation: number;
      net: number;
    };
    total: number;
  };
  liabilities: {
    accountsPayable: number;
    total: number;
  };
  equity: {
    retainedEarnings: number;
    total: number;
  };
  total: number;
  liabilitiesAndEquity: number;
}

export interface FinancialReportData {
  reportType: 'monthly' | 'quarterly' | 'yearly';
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    revenue: number | string;
    expenses: number | string;
    payroll: number | string;
  };
}

export interface DashboardStats {
  totals: {
    clients: number;
    invoices: number;
    expenses: number;
  };
  thisMonth: {
    revenue: number;
    expenses: number;
    payroll: number;
    depreciation: number;
    profit: number;
    profitMargin: number;
  };
  lastMonth: {
    revenue: number;
  };
  outstanding: {
    amount: number;
    count: number;
  };
  pending: {
    timesheets: number;
    expenses: number;
    total: number;
  };
  unreconciled: {
    transactions: number;
  };
  recent: {
    invoices: Array<{
      id: string;
      invoiceNumber: string;
      clientName: string;
      total: number;
      currency: string;
      status: string;
      issueDate: string;
      createdAt: string;
    }>;
    expenses: Array<{
      id: string;
      description: string;
      amount: number;
      currency: string;
      expenseDate: string;
      status: string;
      createdAt: string;
    }>;
    payments: Array<{
      id: string;
      amount: number;
      currency: string;
      invoiceNumber?: string;
      clientName?: string;
      paymentDate: string;
      createdAt: string;
    }>;
  };
}

export interface ContractGrossProfit {
  period: {
    startDate: string;
    endDate: string;
  } | null;
  contracts: Array<{
    clientId: string;
    clientName: string;
    currency: string;
    revenue: number;
    cost: number;
    grossProfit: number;
    grossProfitMargin: number;
    totalHours: number;
    invoiceCount: number;
  }>;
}

export const accountingApi = {
  getProfitLoss: async (startDate: string, endDate: string): Promise<ProfitLossData> => {
    const response = await api.get('/accounting/profit-loss', {
      params: { startDate, endDate },
    });
    return response.data.data;
  },

  getBalanceSheet: async (asOfDate: string): Promise<BalanceSheetData> => {
    const response = await api.get('/accounting/balance-sheet', {
      params: { asOfDate },
    });
    return response.data.data;
  },

  getReports: async (
    reportType: 'monthly' | 'quarterly' | 'yearly',
    startDate: string,
    endDate: string
  ): Promise<FinancialReportData> => {
    const response = await api.get('/accounting/reports', {
      params: { reportType, startDate, endDate },
    });
    return response.data.data;
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get('/accounting/dashboard-stats');
    return response.data.data;
  },

  getContractGrossProfit: async (startDate?: string, endDate?: string): Promise<ContractGrossProfit> => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get('/accounting/contract-gross-profit', { params });
    return response.data.data;
  },
};

